/**
 * EmptyState Component
 * 
 * A component for displaying empty/no-data states.
 */

import React from 'react';
import { Box, Typography } from '@mui/material';
import type { SxProps, Theme } from '@mui/material';
import InboxIcon from '@mui/icons-material/Inbox';
import { colors, typography as typographyTokens, spacing } from '../../tokens';
import { Button } from '../Button';

export interface EmptyStateProps {
  /** Title text */
  title: string;
  /** Description text */
  description?: string;
  /** Custom icon */
  icon?: React.ReactNode;
  /** Action button text */
  actionLabel?: string;
  /** Action click handler */
  onAction?: () => void;
  /** Additional styles */
  sx?: SxProps<Theme>;
}

export const EmptyState: React.FC<EmptyStateProps> = ({
  title,
  description,
  icon,
  actionLabel,
  onAction,
  sx,
}) => (
  <Box
    sx={{
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      textAlign: 'center',
      py: 6,
      px: 3,
      ...sx,
    }}
  >
    {/* Icon */}
    <Box
      sx={{
        width: 64,
        height: 64,
        borderRadius: '50%',
        backgroundColor: colors.neutral[100],
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        color: colors.neutral[400],
        mb: 2,
        '& svg': {
          fontSize: 32,
        },
      }}
    >
      {icon || <InboxIcon />}
    </Box>

    {/* Title */}
    <Typography
      variant="h5"
      sx={{
        fontWeight: typographyTokens.fontWeight.semibold,
        color: colors.neutral[900],
        mb: description ? 1 : 0,
      }}
    >
      {title}
    </Typography>

    {/* Description */}
    {description && (
      <Typography
        variant="body2"
        sx={{
          color: colors.neutral[500],
          maxWidth: 320,
          mb: actionLabel ? 3 : 0,
        }}
      >
        {description}
      </Typography>
    )}

    {/* Action */}
    {actionLabel && onAction && (
      <Button variant="primary" onClick={onAction}>
        {actionLabel}
      </Button>
    )}
  </Box>
);

export default EmptyState;

